/**
 *
 * This constant file is used to share keys across
 * our Front-end apps (HP, MP and Webapp).
 * This is pretty useful to avoid naming issues
 * between those apps when accessing
 * or storing browser storage keys.
 *
 * Each key has a name, a type and a value associated.
 * Some keys doesn't need any value the goal
 * is just to check if the key is present.
 *
 * 'LS' stands for window.localStorage
 * 'ST' stand for sessionStorage
 *
 */
/**
 * @type {String} key
 * @type {String} type
 * @type {Boolean} mandatoryValue
 * @type {Boolean} clearOnLogout
 * @type {Boolean} clearOnLogin
 * @type {Boolean} clearOnRegister
 */
var BrowserStorageConstant = Object.freeze({
    PAGE_VIEWED_PREMIUM: {
        key: 'PAGE_VIEWED_PREMIUM',
        type: 'LS',
        mandatoryValue: false,
        clearOnLogout: false,
        clearOnLogin: false,
        clearOnRegister: true,
    },
    PAGE_VIEWED_PURCHASE: {
        key: 'PAGE_VIEWED_PURCHASE',
        type: 'LS',
        mandatoryValue: false,
        clearOnLogout: false,
        clearOnLogin: false,
        clearOnRegister: true,
    },
    DISCOUNT_COUPON: {
        key: 'DISCOUNT_COUPON',
        type: 'ST',
        mandatoryValue: true,
        clearOnLogout: false,
        clearOnLogin: false,
        clearOnRegister: true,
    },
    BUSUU_PROMO_POPUP: {
        key: 'BUSUU_PROMO_POPUP',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: false,
        clearOnLogin: false,
        clearOnRegister: true,
    },
    SELECTED_PLAN: {
        key: 'SELECTED_PLAN',
        type: 'ST',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    MARKETING_UTM: {
        key: 'MARKETING_UTM',
        type: 'ST',
        mandatoryValue: true,
        clearOnLogout: false,
        clearOnLogin: false,
        clearOnRegister: false,
    },
    REGISTRATION_ONBOARDING: {
        key: 'REGISTRATION_ONBOARDING',
        type: 'ST',
        mandatoryValue: false,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: false,
    },
    KEYBOARD_SHORTCUT_STATUS: {
        key: 'KEYBOARD_SHORTCUT_STATUS',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: false,
        clearOnLogin: false,
        clearOnRegister: true,
    },
    STUDY_PLAN_VIEWED_THIS_SESSION: {
        key: 'STUDY_PLAN_VIEWED_THIS_SESSION',
        type: 'ST',
        mandatoryValue: false,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    STUDY_PLAN_TOTAL_VIEWS: {
        key: 'STUDY_PLAN_TOTAL_VIEWS',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: false,
        clearOnLogin: false,
        clearOnRegister: true,
    },
    STUDY_PLAN_ACTIVATION_DATA: {
        key: 'STUDY_PLAN_ACTIVATION_DATA',
        type: 'ST',
        mandatoryValue: true,
        clearOnLogout: false,
        clearOnLogin: false,
        clearOnRegister: true,
    },
    STUDY_PLAN_FORCE_DROPDOWN: {
        key: 'STUDY_PLAN_FORCE_DROPDOWN',
        type: 'ST',
        mandatoryValue: false,
        clearOnLogout: true,
        clearOnLogin: false,
        clearOnRegister: true,
    },
    STUDY_PLAN_GOAL_VIEWED_THIS_SESSION: {
        key: 'STUDY_PLAN_GOAL_VIEWED_THIS_SESSION',
        type: 'ST',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    ECOMMERCE_ORIGIN: {
        key: 'ECOMMERCE_ORIGIN',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: false,
        clearOnRegister: false,
    },
    /**
     * Used for admin tool to identify which
     * content branch the app is fetching.
     */
    CONTENT_BRANCH: {
        key: 'CONTENT_BRANCH',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: false,
        clearOnLogin: false,
        clearOnRegister: true,
    },
    /*
     * When we send a deeplink to a non-premium user for a premium course
     * store upsell course data to use for the paywall
     */
    COURSES_OVERVIEW_FORCE_UPSELL: {
        key: 'COURSES_OVERVIEW_FORCE_UPSELL',
        type: 'ST',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Used for admin tool to show current content
     * and non ready content as well.
     */
    SHOW_ALL_CONTENT: {
        key: 'SHOW_ALL_CONTENT',
        type: 'LS',
        mandatoryValue: false,
        clearOnLogout: false,
        clearOnLogin: false,
        clearOnRegister: true,
    },
    /**
     * Used for admin tool to disable
     * the cache on the content.
     */
    DISABLE_CONTENT_CACHE: {
        key: 'DISABLE_CONTENT_CACHE',
        type: 'LS',
        mandatoryValue: false,
        clearOnLogout: false,
        clearOnLogin: false,
        clearOnRegister: true,
    },
    /**
     * Used for admin tool to unlock all content
     */
    UNLOCK_ALL_CONTENT: {
        key: 'UNLOCK_ALL_CONTENT',
        type: 'LS',
        mandatoryValue: false,
        clearOnLogout: false,
        clearOnLogin: false,
        clearOnRegister: true,
    },
    /**
     * Used for admin tool to skip friction ads
     */
    SKIP_FRICTION_ADS: {
        key: 'SKIP_FRICTION_ADS',
        type: 'LS',
        mandatoryValue: false,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Used to clear data in media player library
     * We clear this as the library stores things like mute, volume etc...
     * and if the controls are disabled, the user ends up with a non-usable
     * player that they just assume is broken.
     */
    PLYR_STORAGE: {
        key: 'plyr',
        type: 'LS',
        mandatoryValue: false,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Count the number of Free exercise a user did in smart-review
     * So we can show the paywall
     */
    SMART_REVIEW_NB_EXERCISES_DONE: {
        key: 'SMART_REVIEW_NB_EXERCISES_DONE',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: false,
        clearOnLogin: false,
        clearOnRegister: true,
    },
    /**
     * Object describing the trigger status for referral signposts
     */
    REFERRAL_TRIGGERED_STATUS: {
        key: 'REFERRAL_TRIGGERED_STATUS',
        type: 'ST',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Object describing dismissal state and expiry date to reset to default
     */
    REFERRAL_DISMISSED_STATUS: {
        key: 'REFERRAL_DISMISSED_STATUS',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: false,
        clearOnLogin: false,
        clearOnRegister: true,
    },
    /**
     * Saves the unique user referral code
     */
    REFERRAL_USER_CODE: {
        key: 'REFERRAL_USER_CODE',
        type: 'ST',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: false,
    },
    /**
     * Whether or not the user has used their free language swap
     */
    USED_SINGLE_FREE_LANGUAGE_SWITCH: {
        key: 'USED_SINGLE_FREE_LANGUAGE_SWITCH',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: false,
        clearOnLogin: false,
        clearOnRegister: true,
    },
    /**
     * Whether or not the user has interacted with a Live CTA
     * LIVE_ADD_ON_LESSONS
     */
    LIVE_ADD_ON_INTENT: {
        key: 'LIVE_ADD_ON_INTENT',
        type: 'ST',
        mandatoryValue: false,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * When a Live merch item is dismissed,
     * we store a key that has a date value in it.
     * This is used to determine whether it should be
     * shown again based on a certain timeframe.
     */
    LIVE_MERCHANDISING_DISMISSED: {
        key: 'LIVE_MERCHANDISING_DISMISSED',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: false,
        clearOnRegister: false,
    },
    /**
     * Set after a successful payment
     * We can use this key on the Webapp
     * to trigger certain actions post-payment
     */
    POST_PAYMENT_TRIGGER: {
        key: 'POST_PAYMENT_TRIGGER',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: false,
        clearOnLogin: false,
        clearOnRegister: true,
    },
    /**
     * Sets the live source value
     * to local storage
     */
    LIVE_SOURCE: {
        key: 'LIVE_SOURCE',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: false,
        clearOnLogin: false,
        clearOnRegister: false,
    },
    /**
     * Store the state of the live cards
     * to local storage
     */
    LIVE_CARDS_STATE: {
        key: 'LIVE_CARDS_STATE',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: false,
        clearOnLogin: false,
        clearOnRegister: false,
    },
    /**
     * Stores captcha token used by QA
     * to bypass captcha verification
     */
    CAPTCHA_TOKEN: {
        key: 'CAPTCHA_TOKEN',
        type: 'ST',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores the last touchpoint a user came from to correct an exercise
     */
    CORRECTION_SOURCE: {
        key: 'CORRECTION_SOURCE',
        type: 'ST',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores the motivation for learning to be used for study plan setup
     */
    MOTIVATION_FOR_LEARNING: {
        key: 'MOTIVATION_FOR_LEARNING',
        type: 'ST',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    MODAL_LIVE_B2B_STATE: {
        key: 'MODAL_LIVE_B2B_STATE',
        type: 'ST',
        mandatoryValue: true,
        clearOnLogout: false,
        clearOnLogin: false,
        clearOnRegister: false,
    },
    /**
     * Stores whether the user has viewed the streak page
     */
    STREAK_PAGE_VIEWED: {
        key: 'STREAK_PAGE_VIEWED',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores whether the user has watched ad
     */
    AD_WATCHED: {
        key: 'AD_WATCHED',
        type: 'ST',
        mandatoryValue: false,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores whether the user has viewed the ads page
     */
    AD_PAGE_VIEWED: {
        key: 'AD_PAGE_VIEWED',
        type: 'ST',
        mandatoryValue: false,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores whether the user has viewed the lesson ready page
     */
    LESSON_READY_PAGE_VIEWED: {
        key: 'LESSON_READY_PAGE_VIEWED',
        type: 'LS',
        mandatoryValue: false,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores whether the user has completed the challenge today
     */
    DAILY_CHALLENGE_COMPLETED: {
        key: 'DAILY_CHALLENGE_COMPLETED',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores whether the user has seen the lesson ready page with the option to close it
     */
    LESSON_READY_CLOSING_VIEWED: {
        key: 'LESSON_READY_CLOSING_VIEWED',
        type: 'LS',
        mandatoryValue: false,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores the latest progress of dailly challenge
     */
    DAILY_CHALLENGE_LAST_RESULT: {
        key: 'DAILY_CHALLENGE_LAST_RESULT',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores the achieved placement test level
     */
    PLACEMENT_TEST_ACHIEVED_LEVEL: {
        key: 'PLACEMENT_TEST_ACHIEVED_LEVEL',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores the achieved placement test level
     */
    PLACEMENT_TEST_SELECTED_LESSON: {
        key: 'PLACEMENT_TEST_SELECTED_LESSON',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores the lesson completion status
     */
    LESSON_COMPLETION_STATUS: {
        key: 'LESSON_COMPLETION_STATUS',
        type: 'ST',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores whether the user has viewed the league page
     */
    LEAGUE_PAGE_VIEWED: {
        key: 'LEAGUE_PAGE_VIEWED',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores whether the user has viewed the smart review unlocked modal
     */
    SMART_REVIEW_UNLOCKED_MODAL_VIEWED: {
        key: 'SMART_REVIEW_UNLOCKED_MODAL_VIEWED',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores whether the league tooltip has been viewed
     */
    LEAGUE_TOOLTIP_VIEWED: {
        key: 'LEAGUE_TOOLTIP_VIEWED',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores whether the user has viewed the league promotion page
     */
    LEAGUE_PROMOTION_SCREEN_VIEWED: {
        key: 'LEAGUE_PROMOTION_SCREEN_VIEWED',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores the user league position before starting a new lesson
     */
    PREVIOUS_LEAGUE_USER_POSITION: {
        key: 'PREVIOUS_LEAGUE_USER_POSITION',
        type: 'ST',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores a timestamp based on which we redirect to the paywall
     */
    PAYWALL_REDIRECTION_TIMESTAMP: {
        key: 'PAYWALL_REDIRECTION_TIMESTAMP',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: false,
        clearOnLogin: false,
        clearOnRegister: true,
    },
    /**
     * Stores a timestamp for the welcome back sheet
     */
    WELCOME_BACK_SHEET_TIMESTAMP: {
        key: 'WELCOME_BACK_SHEET_TIMESTAMP',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores whether the community tooltip can be shown
     */
    SHOW_COMMUNITY_TOOLTIP: {
        key: 'SHOW_COMMUNITY_TOOLTIP',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores whether the community tooltip can be shown
     */
    DISMISSED_ACHIEVEMENTS_IDS: {
        key: 'DISMISSED_ACHIEVEMENTS_IDS',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores a timestamp for the community kudos sheet
     */
    COMMUNITY_KUDOS_SHEET_TIMESTAMP: {
        key: 'COMMUNITY_KUDOS_SHEET_TIMESTAMP',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores whether the user entered the streak repair flow by viewing the streak paywall
     */
    STREAK_REPAIR_FLOW_ENTERED: {
        key: 'STREAK_REPAIR_FLOW_ENTERED',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores when the user has had their streak repaired
     */
    STREAK_REPAIRED_TIMESTAMP: {
        key: 'STREAK_REPAIRED_TIMESTAMP',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores whether the lock grammar review sheet has been viewed
     */
    LOCK_GRAMMAR_REVIEW_SHEET_VIEWED: {
        key: 'LOCK_GRAMMAR_REVIEW_SHEET_VIEWED',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores whether the user converted through the grammar review flow
     */
    GRAMMAR_REVIEW_FLOW_VIEWED: {
        key: 'GRAMMAR_REVIEW_FLOW_VIEWED',
        type: 'ST',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores the number of unique lessons a user completed
     */
    UNIQUE_LESSONS_COMPLETED: {
        key: 'UNIQUE_LESSONS_COMPLETED',
        type: 'ST',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Saves the unique perks token for returned from perks-integration-service
     */
    PERK_TOKEN: {
        key: 'PERK_TOKEN',
        type: 'ST',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores the rotational index of the internal ad last shown
     */
    INTERNAL_AD_ROTATIONAL_INDEX: {
        key: 'INTERNAL_AD_ROTATIONAL_INDEX',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores whether the user used their vocab review free try
     */
    VOCAB_REVIEW_FREE_TRY_USED: {
        key: 'VOCAB_REVIEW_FREE_TRY_USED',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores whether the user used their grammar review free try
     */
    GRAMMAR_REVIEW_FREE_TRY_USED: {
        key: 'GRAMMAR_REVIEW_FREE_TRY_USED',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores whether the user should go through the delayed onboarding flow https://busuucom.atlassian.net/browse/BOND-3
     */
    DELAYED_ONBOARDING: {
        key: 'DELAYED_ONBOARDING',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores whether the user checklist
     */
    CHECKLIST: {
        key: 'CHECKLIST',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores whether the user checklist
     */
    B2B_NAV_TOOLTIP_SEEN: {
        key: 'B2B_NAV_TOOLTIP_SEEN',
        type: 'LS',
        mandatoryValue: false,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores the auth type in case user is going through SSO via webview. Will store either 'ios' or 'android' types
     */
    AUTH_TYPE: {
        key: 'AUTH_TYPE',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: false,
        clearOnRegister: false,
    },
    /**
     * Stores the url we need to redirect to after abanadoning the paywall
     * from the immersive ad experiment flow
     */
    IMMERSIVE_AD_POST_PAYWALL_REDIRECTION_URL: {
        key: 'IMMERSIVE_AD_POST_PAYWALL_REDIRECTION_URL',
        type: 'ST',
        mandatoryValue: true,
        clearOnLogout: false,
        clearOnLogin: false,
        clearOnRegister: true,
    },
    /**
     * Stores CEFR level that user considers they're on during onboarding.
     */
    SELF_SELECTED_LEVEL: {
        key: 'SELF_SELECTED_LEVEL',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores how many vocabulary review free premium tries the user has completed
     */
    VOCAB_REVIEW_FREE_TRIES_DONE: {
        key: 'VOCAB_REVIEW_FREE_TRIES_COMPLETED',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores how many grammar review free premium tries the user has completed
     */
    GRAMMAR_REVIEW_FREE_TRIES_DONE: {
        key: 'GRAMMAR_REVIEW_FREE_TRIES_COMPLETED',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
    },
    /**
     * Stores a dynamic Learning Progress events batch
     * We need to be able to store multiple batches, so we differentiate it by numbers such as DYNAMIC_LEARNING_PROGRESS_EVENTS_BATCH_1 etc
     */
    DYNAMIC_LEARNING_PROGRESS_EVENTS_BATCH_: {
        key: 'DYNAMIC_LEARNING_PROGRESS_EVENTS_BATCH_',
        type: 'LS',
        mandatoryValue: true,
        clearOnLogout: true,
        clearOnLogin: true,
        clearOnRegister: true,
        isDynamic: true,
    },
});

/**
 * A service to provide data storage using localStorage or sessionStorage.
 * Can be used to get/set/remove any keys.
 */
/**
 * Clears localStorage
 */
var clearLocalStorage = function () {
    try {
        window.localStorage.clear();
    }
    catch (e) {
        // Silence...
    }
};
/**
 * Clears sessionStorage
 */
var clearSessionStorage = function () {
    try {
        window.sessionStorage.clear();
    }
    catch (e) {
        // Silence...
    }
};
/**
 * Returns an associate data Object for a given key.
 * If `isDynamic` is true, it finds the first matching key in `BrowserStorageConstants`
 * that is a prefix of the provided key
 * Eg.: getConstantKeyData('LEARNING_PROGRESS_EVENTS_BATCH_1', true); returns config for 'LEARNING_PROGRESS_EVENTS_BATCH_'
 */
var getConstantKeyData = function (key, isDynamic) {
    if (isDynamic === void 0) { isDynamic = false; }
    var constantKey;
    if (isDynamic) {
        constantKey = Object.keys(BrowserStorageConstant).find(function (storedKey) { return key.startsWith(storedKey); });
    }
    else {
        constantKey = key in BrowserStorageConstant ? key : undefined;
    }
    if (!constantKey) {
        throw new Error("Key '" + key + "' has not been found on browser storage constants list.");
    }
    return BrowserStorageConstant[constantKey];
};
/**
 * Returns all the storage key data. Both LS and ST.
 */
var getAllKeyData = function () {
    return BrowserStorageConstant;
};
/**
 * Returns a given item in browser storage (LS or ST) from a key value
 */
var getStorageItem = function (key, isDynamic) {
    try {
        var keyData = getConstantKeyData(key, isDynamic);
        var type = keyData.type;
        switch (type) {
            case 'LS':
                return window.localStorage.getItem(key) || null;
            case 'ST':
                return window.sessionStorage.getItem(key) || null;
        }
    }
    catch (e) {
        return null;
    }
};
/**
 * Helper function that returns an object mapping keys to their corresponding values from a given Storage,
 * but only includes items whose keys start with the given prefix.
 *
 * Eg.:
 *  - We have 'DYNAMIC_KEY_1' and 'DYNAMIC_KEY_2' in the local storage
 *  - getStorageItemsByPrefix('DYNAMIC_KEY_') will return both
 */
var getStorageItemsByPrefix = function (storage, prefix) {
    var entries = Object.entries(storage);
    var filteredEntries = entries.filter(function (_a) {
        var key = _a[0];
        return key.startsWith(prefix);
    });
    if (filteredEntries.length === 0) {
        return null;
    }
    // Rebuild an object from the filtered entries
    var objectFromEntries = filteredEntries.reduce(function (acc, _a) {
        var key = _a[0], value = _a[1];
        acc[key] = value;
        return acc;
    }, {});
    return objectFromEntries;
};
/**
 * Retrieves all storage items from either localStorage or sessionStorage that match a given prefix
 */
var getAllStorageItemsByPrefix = function (key) {
    try {
        var keyData = getConstantKeyData(key, true);
        var type = keyData.type;
        switch (type) {
            case 'LS':
                return getStorageItemsByPrefix(window.localStorage, key);
            case 'ST':
                return getStorageItemsByPrefix(window.sessionStorage, key);
        }
    }
    catch (e) {
        return null;
    }
};
/**
 * Set a given key/value in the localStorage
 */
var setStorageItem = function (key, value, isDynamic) {
    try {
        var keyData = getConstantKeyData(key, isDynamic);
        var type = keyData.type, mandatoryValue = keyData.mandatoryValue;
        var val = mandatoryValue && value ? value : key;
        switch (type) {
            case 'LS':
                window.localStorage.setItem(key, val);
                break;
            case 'ST':
                window.sessionStorage.setItem(key, val);
                break;
        }
    }
    catch (e) {
        // Silence...
    }
};
/**
 * For static constants we want to directly remove the value from the storage
 * For dynamic storage keys, we want to loop through all the keys to delete all of them, unless "exact" property is set
 * Eg.: We have a constant NEW_DYNAMIC_CONSTANT_ in BrowserStorageConstants with isDynamic field set to true
 *      In our browser we store NEW_DYNAMIC_CONSTANT_1, NEW_DYNAMIC_CONSTANT_2, NEW_DYNAMIC_CONSTANT_3
 *      Using this function we will delete all of these constants when calling removeStorageItem('NEW_DYNAMIC_CONSTANT_')
 */
var removeKeysFromStorage = function (storage, key, exact) {
    if (exact) {
        storage.removeItem(key);
    }
    else {
        Object.keys(storage).forEach(function (storageKey) {
            if (storageKey.startsWith(key)) {
                storage.removeItem(storageKey);
            }
        });
    }
};
/**
 * Delete a given item in localStorage from a key value
 * usePrefixRemoval - remove a dynamic item by prefix.
 * Eg.: We have DYNAMIC_ITEM_ dynamic constant. DYNAMIC_ITEM_1 and DYNAMIC_ITEM_2 are stored in LocalStorage
 *      We can call removeStorageItem('DYNAMIC_ITEM_1', true) to remove only DYNAMIC_ITEM_1 while preserving DYNAMIC_ITEM_2
 */
var removeStorageItem = function (key, usePrefixRemoval) {
    try {
        var keyData = getConstantKeyData(key, usePrefixRemoval);
        var type = keyData.type, isDynamic = keyData.isDynamic;
        var exact = usePrefixRemoval || !isDynamic; // if it's dynamic, default to prefix removal
        switch (type) {
            case 'LS':
                removeKeysFromStorage(window.localStorage, key, exact);
                break;
            case 'ST':
                removeKeysFromStorage(window.sessionStorage, key, exact);
                break;
        }
    }
    catch (e) {
        // Silence...
    }
};

export { BrowserStorageConstant as BrowserStorageConstants, clearLocalStorage, clearSessionStorage, getAllKeyData, getAllStorageItemsByPrefix, getConstantKeyData, getStorageItem, removeStorageItem, setStorageItem };
//# sourceMappingURL=index.esm.js.map
